'use client';
import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Play, ChevronDown, Award, Star, Film } from 'lucide-react';
import { HERO_BACKGROUND } from '@/lib/mediaConfig';

export default function HeroSection() {
  const [year, setYear] = useState('2026');
  const [scrollY, setScrollY] = useState(0);
  const [mounted, setMounted] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setYear(new Date()?.getFullYear()?.toString());
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef?.current) {
        const rect = sectionRef?.current?.getBoundingClientRect();
        if (rect?.top < window.innerHeight && rect?.bottom > 0) {
          setScrollY(window.scrollY);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const parallaxOffset = mounted ? scrollY * 0.4 : 0;

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Cinematic background with parallax */}
      <div
        className="absolute inset-0 will-change-transform"
        style={{ transform: `translateY(${parallaxOffset}px) scale(1.15)` }}
      >
        <Image
          src={HERO_BACKGROUND?.src}
          alt={HERO_BACKGROUND?.alt}
          fill
          priority
          quality={90}
          className="object-cover object-center"
          sizes="100vw"
        />
        {/* Cinematic dark overlay with gold tint */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
        {/* Gold atmospheric glow */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(201,168,76,0.12) 0%, transparent 70%)' }} />
      </div>
      {/* Noise texture overlay */}
      <div className="absolute inset-0 noise-overlay pointer-events-none opacity-30" />
      {/* Floating badges */}
      <div className="absolute top-32 left-8 lg:left-24 glass-card px-4 py-2.5 rounded-2xl flex items-center gap-2 animate-fade-in hidden md:flex" style={{ backdropFilter: 'blur(20px)', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(201,168,76,0.3)' }}>
        <Award size={16} className="text-primary" />
        <span className="text-xs font-semibold text-white/80">India&apos;s Premier Media Awards</span>
      </div>
      <div className="absolute top-48 right-8 lg:right-24 glass-card px-4 py-2.5 rounded-2xl flex items-center gap-2 hidden md:flex" style={{ backdropFilter: 'blur(20px)', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(201,168,76,0.3)', animationDelay: '0.3s' }}>
        <Star size={14} className="text-primary fill-primary" />
        <span className="text-xs font-semibold text-white/80">2,400+ Entries in {year}</span>
      </div>
      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 text-center pt-20">
        {/* Eyebrow */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)' }}>
          <Film size={14} className="text-primary" />
          <span className="text-xs font-semibold text-primary tracking-widest uppercase">Season 3 — {year}</span>
        </div>

        {/* Title */}
        <h1 className="hero-title text-white mb-6 max-w-5xl mx-auto" style={{ textShadow: '0 4px 32px rgba(0,0,0,0.8)' }}>
          <span className="block">Where Great</span>
          <span className="gradient-gold-text block">Stories</span>
          <span className="block">Get Recognized</span>
        </h1>

        {/* Subtitle */}
        <p className="text-lg lg:text-xl text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed font-medium" style={{ textShadow: '0 2px 16px rgba(0,0,0,0.6)' }}>
          Motion Max Media Awards celebrates extraordinary talent in filmmaking,
          short films, reels, and digital storytelling across India.
        </p>

        {/* Stats row */}
        <div className="flex items-center justify-center gap-8 mb-12">
          {[
            { value: '₹5L+', label: 'Prize Pool' },
            { value: '18', label: 'Categories' },
            { value: '50+', label: 'Expert Judges' },
            { value: '2,400+', label: 'Past Entries' },
          ]?.map((stat) => (
            <div key={`hero-stat-${stat?.label}`} className="text-center hidden sm:block">
              <p className="text-xl lg:text-2xl font-extrabold gradient-gold-text tabular-nums">{stat?.value}</p>
              <p className="text-xs text-white/60 font-medium mt-0.5">{stat?.label}</p>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <Link
            href="/register"
            className="btn-primary px-8 py-4 rounded-full text-base font-bold flex items-center gap-2 glow-gold-sm min-w-48 justify-center"
          >
            <Award size={18} />
            Join Now — Free
          </Link>
          <button
            className="px-8 py-4 rounded-full text-base font-semibold flex items-center gap-2 min-w-48 justify-center transition-all hover:bg-white/10"
            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', backdropFilter: 'blur(12px)' }}
          >
            <Play size={16} fill="currentColor" />
            Watch Reel
          </button>
        </div>

        {/* Deadline banner */}
        <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl mb-16" style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(201,168,76,0.3)', backdropFilter: 'blur(16px)' }}>
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-sm font-medium text-white/70">
            Submissions open — Deadline: <span className="text-white font-bold">31 August {year}</span>
          </span>
        </div>

        {/* Scroll indicator */}
        <div className="flex flex-col items-center gap-2 text-white/40">
          <span className="text-xs font-medium tracking-widest uppercase">Scroll to explore</span>
          <ChevronDown size={18} className="animate-bounce" />
        </div>
      </div>
      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
}